/**
 * Product detail — Showroom style, DYNAMIC schema (deep-link target
 * `product/{id}`). Shows the sheet's own columns in the admin-configured
 * order (product.attrs); no prices anywhere. Legacy field groups remain as a
 * fallback for old data.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { Product, SpecTableRow } from '@mitang/shared';
import { specTableRows } from '@mitang/shared';
import { AppBar } from '../../components/AppBar';
import { Spinner } from '../../components/Spinner';
import { listenProduct } from '../../lib/repo';
import { formatPriceCell, relativeTime } from '../../lib/format';
import { buildShareText, shareProductOnWhatsApp } from './share';
import { useTabDisplayFormat } from '../../hooks/useDisplayFormat';
import { useCategoryProducts } from '../../hooks/useCategoryProducts';
import { useProductPrice } from '../../hooks/usePrices';
import { logActivity } from '../../lib/activity/activityLogger';
import { setPresenceView } from '../../lib/presence';
import { ROUTES } from '../../routes/paths';
import { backgroundOf, useOpenProduct } from '../../routes/productOverlay';
import { useWishlist } from '../../hooks/useWishlist';
import { HeartButton } from '../../components/HeartButton';

const LEGACY_FIELDS: Array<[keyof Product, string]> = [
  ['processor', 'Processor'],
  ['generation', 'Generation'],
  ['ram', 'RAM'],
  ['storage', 'Storage'],
  ['graphics', 'Graphics'],
  ['os', 'OS'],
  ['display', 'Display'],
  ['keyboard', 'Keyboard'],
  ['colour', 'Colour'],
  ['bag', 'Bag'],
  ['upgradeOption', 'Upgrade option'],
];

/** Show the freshness note only while the row is genuinely fresh. */
const FRESH_WINDOW_MS = 6 * 60 * 60 * 1000;

/** Spec rows: the sheet's columns in order (minus title/part duplicates). */
function specRows(product: Product): Array<[string, string]> {
  if (product.attrs && product.attrs.length > 0) {
    return product.attrs
      .filter((a) => a.v !== product.modelNumber && a.v !== product.partNo)
      .map((a) => [a.k, a.v] as [string, string]);
  }
  return LEGACY_FIELDS.map(([key, label]) => [label, product[key]] as const).filter(
    (r): r is [string, string] => typeof r[1] === 'string' && r[1] !== '',
  );
}

export function ProductDetailScreen(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const overlay = backgroundOf(useLocation()) !== undefined;
  const openProduct = useOpenProduct();
  const [product, setProduct] = useState<Product | null | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  // Prices resolve only for admin-allow-listed users; null for everyone else.
  const priceAttrs = useProductPrice(id ? decodeURIComponent(id) : null);
  const wishlist = useWishlist();
  // Admin presentation config (labels, table gaps, share layout) — live.
  const fmt = useTabDisplayFormat(product?.category);

  useEffect(() => {
    if (!id) return;
    const productId = decodeURIComponent(id);
    const unsub = listenProduct(
      productId,
      (p) => setProduct(p),
      () => setProduct(null),
    );
    return () => unsub();
  }, [id]);

  useEffect(() => {
    if (product) {
      // category too, so the admin's log export can pivot by sheet without
      // having to infer it from the product id.
      logActivity('viewed_product', {
        productId: product.id,
        modelNumber: product.modelNumber,
        category: product.category,
      });
      setPresenceView({
        view: `product:${product.id}`,
        label: `Viewing ${product.modelNumber}`,
        productId: product.id,
      });
    }
  }, [product]);

  // Siblings for the "More in <series>" strip (shared category listener).
  const { products: categoryProducts } = useCategoryProducts(product?.category ?? null);
  const siblings = useMemo(() => {
    if (!product) return [];
    const sameSeries = categoryProducts.filter(
      (p) => p.id !== product.id && p.series && p.series === product.series,
    );
    const pool = sameSeries.length > 0
      ? sameSeries
      : categoryProducts.filter((p) => p.id !== product.id);
    return pool.slice(0, 6);
  }, [categoryProducts, product]);

  const copy = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };

  if (product === undefined) {
    return (
      <div className="flex min-h-full flex-1 flex-col bg-ground">
        <AppBar />
        <Spinner label="Loading…" />
      </div>
    );
  }

  if (product === null) {
    return (
      <div className="flex min-h-full flex-1 flex-col bg-ground">
        <AppBar />
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-slate-400">
          <p className="text-sm">This product is no longer available.</p>
          <button type="button" className="btn-secondary" onClick={() => navigate(ROUTES.home)}>
            Back to catalog
          </button>
        </div>
      </div>
    );
  }

  const fresh = Date.now() - product.updatedAt < FRESH_WINDOW_MS;
  // Price rows are appended to the spec sheet too (allow-listed users only), so
  // everything a dealer quotes from is in one place.
  const rows: SpecTableRow[] = [
    ...specTableRows(specRows(product), fmt),
    ...(priceAttrs ?? []).map((a) => ({
      key: a.k,
      label: a.k,
      value: formatPriceCell(a.v),
      breakBefore: false,
    })),
  ];

  return (
    <div className="flex min-h-full flex-1 flex-col bg-ground">
      <nav className="flex items-center justify-between px-4 pb-1 pt-3">
        <button
          type="button"
          className="text-[13px] font-semibold text-slate-500"
          onClick={() =>
            // Opened over a list: just close the layer (back to the list as it
            // was). Opened directly (push / deep link): go to its category.
            overlay
              ? navigate(-1)
              : navigate(ROUTES.category(product.category), { replace: true })
          }
        >
          ‹ {product.category}
        </button>
      </nav>

      <div className="flex-1 overflow-y-auto pb-2">
        <div className="px-5 pt-1.5">
          <p className="text-[10.5px] font-bold uppercase tracking-[.12em] text-slate-400">
            {[product.series, product.sectionTitle].filter(Boolean).join(' · ') || product.category}
          </p>
          <h1 className="mt-1 text-[23px] font-extrabold leading-tight tracking-tight text-ink">
            {product.modelNumber}
          </h1>
          <div className="mt-1 flex items-center gap-2">
            {product.partNo && product.partNo !== product.modelNumber ? (
              <span className="font-mono text-xs text-slate-400">{product.partNo}</span>
            ) : null}
            {fresh ? (
              <span className="text-[11px] font-semibold text-drop">
                ● Updated {relativeTime(product.updatedAt)}
              </span>
            ) : null}
          </div>
        </div>

        {product.remarks ? (
          <div className="mt-3 px-5">
            <span
              className={`rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-[.08em] ${
                /last|few|left|only/i.test(product.remarks)
                  ? 'bg-hot text-white'
                  : 'bg-amber-50 text-stale'
              }`}
            >
              {product.remarks}
            </span>
          </div>
        ) : null}

        {priceAttrs ? (
          <section className="mx-5 mt-4 rounded-[18px] bg-white px-5 py-4 shadow-card">
            <p className="text-[10px] font-bold uppercase tracking-[.14em] text-slate-300">
              {priceAttrs[0].k}
            </p>
            <p className="mt-0.5 text-[30px] font-extrabold tracking-tight text-ink-price [font-variant-numeric:tabular-nums]">
              {formatPriceCell(priceAttrs[0].v)}
            </p>
            {priceAttrs.slice(1).map((a) => (
              <p key={a.k} className="mt-1 text-[12px] text-slate-500">
                <span className="text-slate-400">{a.k}: </span>
                {formatPriceCell(a.v)}
              </p>
            ))}
          </section>
        ) : null}

        <section className="mx-5 mt-4 rounded-[18px] bg-white px-5 py-2 shadow-card">
          <dl>
            {rows.map((row, i) => (
              <div key={`${row.key}-${i}`}>
                {row.breakBefore ? <div className="-mx-5 h-1.5 bg-ground" /> : null}
                <div
                  className={`flex justify-between gap-4 py-2.5 text-[13px] ${
                    i > 0 && !row.breakBefore ? 'border-t border-slate-100' : ''
                  }`}
                >
                  <dt className="shrink-0 text-slate-400">{row.label}</dt>
                  <dd className="text-right font-semibold text-ink">{row.value}</dd>
                </div>
              </div>
            ))}
          </dl>
        </section>

        {siblings.length > 0 ? (
          <section className="pt-4">
            <h2 className="mb-2 px-5 text-[10px] font-bold uppercase tracking-[.16em] text-slate-300">
              More in {product.series ?? product.category}
            </h2>
            <div className="flex gap-2.5 overflow-x-auto px-5 pb-2">
              {siblings.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="min-w-[142px] shrink-0 rounded-[14px] bg-white p-3 text-left shadow-mini"
                  onClick={() => openProduct(s.id)}
                >
                  <p className="truncate text-[11.5px] font-bold leading-tight text-ink">
                    {s.modelNumber}
                  </p>
                  <p className="truncate font-mono text-[9.5px] text-slate-400">{s.partNo ?? ''}</p>
                  <p className="mt-1 truncate text-[10.5px] text-slate-500">
                    {(s.attrs?.[1]?.v ?? s.processor ?? '')}
                  </p>
                </button>
              ))}
            </div>
          </section>
        ) : null}
      </div>

      <div className="sticky bottom-0 flex gap-2.5 bg-gradient-to-t from-ground via-ground to-transparent px-4 pb-9 pt-2">
        <HeartButton
          variant="bar"
          saved={wishlist.isSaved(product.id)}
          onToggle={() => wishlist.toggle(product)}
        />
        <button
          type="button"
          className="flex-1 rounded-full bg-wa py-3 text-center text-[14.5px] font-bold text-white shadow-bar"
          onClick={() => shareProductOnWhatsApp(product, priceAttrs, fmt)}
        >
          Share on WhatsApp
        </button>
        <button
          type="button"
          aria-label="Copy details"
          className="flex min-w-[64px] shrink-0 items-center justify-center rounded-full bg-white px-3 text-[12px] font-semibold text-ink shadow-mini"
          onClick={() => void copy(buildShareText(product, priceAttrs, fmt))}
        >
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
      </div>
    </div>
  );
}
